import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Mail, Phone, Link2, MessageSquare } from "lucide-react";

const EMPTY = { name: "", title: "", email: "", phone: "", linkedin: "", notes: "", hiring_authority: "", is_primary: false };

export function ContactsPanel({ clientId, contacts, canWrite, onChanged }) {
  const [sheet, setSheet] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const open = (contact) => { setSheet(contact || {}); setForm(contact ? { ...EMPTY, ...Object.fromEntries(Object.entries(contact).map(([k, v]) => [k, v ?? ""])), is_primary: !!contact.is_primary } : EMPTY); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Contact name is required"); return; }
    const payload = { name: form.name, title: form.title || null, email: form.email || null, phone: form.phone || null, linkedin: form.linkedin || null, notes: form.notes || null, hiring_authority: form.hiring_authority || null, is_primary: !!form.is_primary };
    setSaving(true);
    try {
      if (sheet?.id) await api.put(`/clients/${clientId}/contacts/${sheet.id}`, payload);
      else await api.post(`/clients/${clientId}/contacts`, payload);
      toast.success("Contact saved"); setSheet(null); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="contacts-panel">
      {canWrite && <div className="flex justify-end"><Button size="sm" className="gap-1.5 bg-[#255058] text-white hover:bg-[#1c3d43]" onClick={() => open(null)} data-testid="contact-create"><Plus className="h-4 w-4" /> Add POC</Button></div>}
      <div className="grid gap-3 sm:grid-cols-2">
        {(contacts || []).map((ct) => (
          <Card key={ct.id} className="p-4" data-testid={`contact-card-${ct.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><span className="font-medium">{ct.name}</span>{ct.is_primary && <Badge variant="outline" className="text-[9px]">Primary</Badge>}</div>
                <div className="text-xs text-muted-foreground">{ct.title || "—"}</div>
                {ct.email && <div className="mt-1.5 flex items-center gap-1.5 text-sm"><Mail className="h-3.5 w-3.5 text-[#255058]" />{ct.email}</div>}
                {ct.phone && <div className="flex items-center gap-1.5 text-sm"><Phone className="h-3.5 w-3.5 text-[#255058]" />{ct.phone}</div>}
                {ct.linkedin && <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Link2 className="h-3.5 w-3.5" />{ct.linkedin}</div>}
                {ct.notes && <div className="mt-1.5 text-xs text-muted-foreground">{ct.notes}</div>}
              </div>
              {canWrite && <Button size="sm" variant="outline" onClick={() => open(ct)} data-testid={`contact-edit-${ct.id}`}>Edit</Button>}
            </div>
          </Card>
        ))}
        {!(contacts || []).length && <div className="text-sm text-muted-foreground">No contacts.</div>}
      </div>

      <Sheet open={!!sheet} onOpenChange={(o) => !o && setSheet(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="contact-sheet">
          <SheetHeader><SheetTitle className="font-display">{sheet?.id ? "Edit Contact" : "New Contact / POC"}</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-4 pb-8">
            <F label="Name *"><Input data-testid="ct-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></F>
            <F label="Email"><Input data-testid="ct-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
            <F label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
            <F label="LinkedIn"><Input value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} /></F>
            <F label="Hiring authority"><Input value={form.hiring_authority} onChange={(e) => setForm({ ...form, hiring_authority: e.target.value })} /></F>
            <F label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="ct-primary" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} /> Primary contact</label>
            <Button className="w-full bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={saving} onClick={save} data-testid="ct-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Contact"}</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function CommunicationsPanel({ clientId, contacts, canWrite }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ channel: "call", direction: "outbound", contact_id: "", summary: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/clients/${clientId}/communications`); setItems(data.items || []); }
    catch (e) { toast.error(apiErr(e)); setItems([]); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.summary.trim()) { toast.error("Add a short summary"); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/communications`, { channel: form.channel, direction: form.direction, contact_id: form.contact_id || null, summary: form.summary });
      toast.success("Communication logged"); setForm({ ...form, summary: "" }); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="communications-panel">
      {canWrite && (
        <Card className="p-4">
          <div className="mb-3 flex items-center gap-2 font-display font-semibold"><MessageSquare className="h-4 w-4 text-[#255058]" /> Log communication</div>
          <div className="grid gap-3 sm:grid-cols-4">
            <F label="Channel">
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger data-testid="comm-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{["call", "email", "meeting", "note", "linkedin", "whatsapp"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Direction">
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["outbound", "inbound"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Contact">
              <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="comm-contact"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not specified</SelectItem>{(contacts || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <div className="flex items-end"><Button className="w-full bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={saving} onClick={save} data-testid="comm-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log"}</Button></div>
            <div className="sm:col-span-4"><F label="Summary *"><Textarea rows={2} data-testid="comm-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></F></div>
          </div>
        </Card>
      )}
      <Card className="p-5">
        <div className="mb-3 font-display font-semibold">Communication history</div>
        <div className="space-y-3">
          {items === null ? <div className="text-sm text-muted-foreground">Loading…</div>
            : items.length ? items.map((a) => (
              <div key={a.id} className="flex gap-3 text-sm" data-testid={`comm-item-${a.id}`}>
                <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#255058]" />
                <div><div>{a.summary}</div>
                  <div className="text-[11px] text-muted-foreground">{a.meta?.contact_name ? `${a.meta.contact_name} · ` : ""}{a.actor_name} · {fmtDate(a.created_at)}</div></div>
              </div>
            )) : <div className="text-sm text-muted-foreground">No communication logged yet.</div>}
        </div>
      </Card>
    </div>
  );
}

function F({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>; }
